import { defineStore } from 'pinia';
import { config } from '@/config';

export const useBulkAnalysisStore = defineStore('bulkAnalysis', {
  state: () => ({
    bulkJobs: [],
    currentJob: null,
    currentJobAnalyses: [],
    loading: false,
    uploading: false,
    uploadProgress: 0,
    error: null,
    totalJobs: 0,
    totalJobAnalyses: 0,
    currentPage: 1,
    limit: 10,
    pollingIntervals: new Map(), // Map of jobId -> intervalId
  }),

  actions: {
    /**
     * Upload a file and create a bulk analysis job
     * @param {File} file - Excel/CSV file
     * @param {string} userId - Firebase UID
     * @param {string} jobName - Optional name for the job
     */
    async uploadBulkJob(file, userId, jobName = null) {
      this.uploading = true;
      this.uploadProgress = 0;
      this.error = null;

      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('user_id', userId);
        if (jobName) {
          formData.append('job_name', jobName);
        }

        const response = await fetch(`${config.API_BASE_URL}/bulk-analyses`, {
          method: 'POST',
          headers: {
            'X-User-Id': userId,
          },
          body: formData,
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create bulk job');
        }

        const data = await response.json();
        this.uploading = false;
        this.uploadProgress = 100;

        // Refresh the jobs list
        await this.fetchUserBulkJobs(userId);

        return data;
      } catch (error) {
        this.error = error.message;
        this.uploading = false;
        this.uploadProgress = 0;
        console.error('Error uploading bulk job:', error);
        throw error;
      }
    },

    /**
     * Fetch all bulk jobs for a user
     * @param {string} userId - Firebase UID
     * @param {object} options - Query options (status, limit, offset)
     */
    async fetchUserBulkJobs(userId, options = {}) {
      this.loading = true;
      this.error = null;

      try {
        const { status, limit = this.limit, offset = 0 } = options;

        let url = `${config.API_BASE_URL}/bulk-analyses/user/${userId}?limit=${limit}&offset=${offset}`;

        if (status) {
          url += `&status=${status}`;
        }

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-User-Id': userId,
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to fetch bulk jobs');
        }

        const data = await response.json();
        this.bulkJobs = data.bulk_jobs;
        this.totalJobs = data.total;
        this.loading = false;

        return data;
      } catch (error) {
        this.error = error.message;
        this.loading = false;
        console.error('Error fetching bulk jobs:', error);
        throw error;
      }
    },

    /**
     * Fetch a single bulk job by ID
     * @param {string} jobId - Job UUID
     * @param {string} userId - User ID for authorization
     */
    async fetchBulkJobById(jobId, userId) {
      this.loading = true;
      this.error = null;

      try {
        const response = await fetch(
          `${config.API_BASE_URL}/bulk-analyses/${jobId}?user_id=${userId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'X-User-Id': userId,
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to fetch bulk job');
        }

        const data = await response.json();
        this.currentJob = data;

        // Update in list if exists
        const index = this.bulkJobs.findIndex((j) => j.job_id === jobId);
        if (index !== -1) {
          this.bulkJobs[index] = data;
        }

        this.loading = false;
        return data;
      } catch (error) {
        this.error = error.message;
        this.loading = false;
        console.error('Error fetching bulk job:', error);
        throw error;
      }
    },

    /**
     * Fetch analyses for a bulk job
     * @param {string} jobId - Job UUID
     * @param {string} userId - User ID
     * @param {object} options - Query options
     */
    async fetchBulkJobAnalyses(jobId, userId, options = {}) {
      try {
        const { status, limit = 50, offset = 0 } = options;

        let url = `${config.API_BASE_URL}/bulk-analyses/${jobId}/analyses?limit=${limit}&offset=${offset}`;

        if (status) {
          url += `&status=${status}`;
        }

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-User-Id': userId,
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to fetch job analyses');
        }

        const data = await response.json();
        this.currentJobAnalyses = data.analyses;
        this.totalJobAnalyses = data.total;

        return data;
      } catch (error) {
        console.error('Error fetching bulk job analyses:', error);
        throw error;
      }
    },

    /**
     * Pause a bulk job
     * @param {string} jobId - Job UUID
     * @param {string} userId - User ID
     */
    async pauseJob(jobId, userId) {
      this.loading = true;
      this.error = null;

      try {
        const response = await fetch(
          `${config.API_BASE_URL}/bulk-analyses/${jobId}/pause`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-User-Id': userId,
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to pause job');
        }

        const data = await response.json();

        // Update local state
        const index = this.bulkJobs.findIndex((j) => j.job_id === jobId);
        if (index !== -1) {
          this.bulkJobs[index].status = 'paused';
        }
        if (this.currentJob && this.currentJob.job_id === jobId) {
          this.currentJob.status = 'paused';
        }

        this.loading = false;
        return data;
      } catch (error) {
        this.error = error.message;
        this.loading = false;
        console.error('Error pausing job:', error);
        throw error;
      }
    },

    /**
     * Resume a paused bulk job
     * @param {string} jobId - Job UUID
     * @param {string} userId - User ID
     */
    async resumeJob(jobId, userId) {
      this.loading = true;
      this.error = null;

      try {
        const response = await fetch(
          `${config.API_BASE_URL}/bulk-analyses/${jobId}/resume`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-User-Id': userId,
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to resume job');
        }

        const data = await response.json();

        // Update local state
        const index = this.bulkJobs.findIndex((j) => j.job_id === jobId);
        if (index !== -1) {
          this.bulkJobs[index].status = 'processing';
        }
        if (this.currentJob && this.currentJob.job_id === jobId) {
          this.currentJob.status = 'processing';
        }

        this.loading = false;
        return data;
      } catch (error) {
        this.error = error.message;
        this.loading = false;
        console.error('Error resuming job:', error);
        throw error;
      }
    },

    /**
     * Cancel a bulk job
     * @param {string} jobId - Job UUID
     * @param {string} userId - User ID
     */
    async cancelJob(jobId, userId) {
      this.loading = true;
      this.error = null;

      try {
        const response = await fetch(
          `${config.API_BASE_URL}/bulk-analyses/${jobId}/cancel`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-User-Id': userId,
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to cancel job');
        }

        const data = await response.json();

        // Update local state
        const index = this.bulkJobs.findIndex((j) => j.job_id === jobId);
        if (index !== -1) {
          this.bulkJobs[index].status = 'cancelled';
        }
        if (this.currentJob && this.currentJob.job_id === jobId) {
          this.currentJob.status = 'cancelled';
        }

        // Stop polling if active
        this.stopPolling(jobId);

        this.loading = false;
        return data;
      } catch (error) {
        this.error = error.message;
        this.loading = false;
        console.error('Error cancelling job:', error);
        throw error;
      }
    },

    /**
     * Delete a bulk job
     * @param {string} jobId - Job UUID
     * @param {string} userId - User ID
     */
    async deleteJob(jobId, userId) {
      this.loading = true;
      this.error = null;

      try {
        const response = await fetch(
          `${config.API_BASE_URL}/bulk-analyses/${jobId}`,
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              'X-User-Id': userId,
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to delete job');
        }

        const data = await response.json();

        // Remove from local list
        this.bulkJobs = this.bulkJobs.filter((j) => j.job_id !== jobId);
        this.totalJobs -= 1;

        // Stop polling if active
        this.stopPolling(jobId);

        this.loading = false;
        return data;
      } catch (error) {
        this.error = error.message;
        this.loading = false;
        console.error('Error deleting job:', error);
        throw error;
      }
    },

    /**
     * Download results for a bulk job
     * @param {string} jobId - Job UUID
     * @param {string} userId - User ID
     */
    async downloadResults(jobId, userId) {
      try {
        const response = await fetch(
          `${config.API_BASE_URL}/bulk-analyses/${jobId}/download?user_id=${userId}`,
          {
            method: 'GET',
            headers: {
              'X-User-Id': userId,
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to download results');
        }

        // Get filename from Content-Disposition header or use default
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'bulk_analysis_results.xlsx';
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?([^"]+)"?/);
          if (match) {
            filename = match[1];
          }
        }

        // Create blob and download
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        return { success: true, filename };
      } catch (error) {
        console.error('Error downloading results:', error);
        throw error;
      }
    },

    /**
     * Start polling for a bulk job
     * @param {string} jobId - Job UUID
     * @param {string} userId - User ID
     * @param {function} onUpdate - Callback when job status updates
     */
    startPolling(jobId, userId, onUpdate = null) {
      // Don't start if already polling
      if (this.pollingIntervals.has(jobId)) {
        return;
      }

      const intervalId = setInterval(async () => {
        try {
          const job = await this.fetchBulkJobById(jobId, userId);

          if (onUpdate) {
            onUpdate(job);
          }

          // Stop polling if job is in terminal state
          if (['completed', 'cancelled', 'failed'].includes(job.status)) {
            this.stopPolling(jobId);
          }
        } catch (error) {
          console.error('Polling error:', error);
        }
      }, 5000); // Poll every 5 seconds

      this.pollingIntervals.set(jobId, intervalId);
    },

    /**
     * Stop polling for a job
     * @param {string} jobId - Job UUID
     */
    stopPolling(jobId) {
      const intervalId = this.pollingIntervals.get(jobId);
      if (intervalId) {
        clearInterval(intervalId);
        this.pollingIntervals.delete(jobId);
      }
    },

    /**
     * Stop all active polling
     */
    stopAllPolling() {
      this.pollingIntervals.forEach((intervalId) => {
        clearInterval(intervalId);
      });
      this.pollingIntervals.clear();
    },

    /**
     * Set current page
     * @param {number} page - Page number
     */
    setCurrentPage(page) {
      this.currentPage = page;
    },

    /**
     * Reset store state
     */
    reset() {
      this.stopAllPolling();
      this.bulkJobs = [];
      this.currentJob = null;
      this.currentJobAnalyses = [];
      this.loading = false;
      this.uploading = false;
      this.uploadProgress = 0;
      this.error = null;
      this.totalJobs = 0;
      this.totalJobAnalyses = 0;
      this.currentPage = 1;
    },
  },

  getters: {
    /**
     * Get total number of pages
     */
    totalPages: (state) => {
      return Math.ceil(state.totalJobs / state.limit);
    },

    /**
     * Get active (processing/pending) jobs
     */
    activeJobs: (state) => {
      return state.bulkJobs.filter((j) =>
        ['pending', 'processing', 'paused'].includes(j.status)
      );
    },

    /**
     * Get completed jobs
     */
    completedJobs: (state) => {
      return state.bulkJobs.filter((j) => j.status === 'completed');
    },

    /**
     * Check if user has an active job
     */
    hasActiveJob: (state) => {
      return state.bulkJobs.some((j) =>
        ['pending', 'processing', 'paused'].includes(j.status)
      );
    },

    /**
     * Get current job progress percentage
     */
    currentJobProgress: (state) => {
      if (!state.currentJob) return 0;
      return state.currentJob.progress_percentage || 0;
    },
  },
});
